import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Node, mergeAttributes } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import { useCallback, useEffect, useRef } from 'react'
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code, Link as LinkIcon, ImageIcon,
  Video as VideoIcon, RemoveFormatting,
} from 'lucide-react'

const VideoNode = Node.create({
  name: 'video',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
    }
  },

  parseHTML() {
    return [{ tag: 'video' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['video', mergeAttributes(HTMLAttributes, {
      controls: true,
      class: 'rounded-lg max-w-full',
      preload: 'metadata',
    })]
  },
})

const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-gray-200 text-gray-900' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
    }`}
  >
    {children}
  </button>
)

const Divider = () => <div className="w-px h-5 bg-gray-300 mx-0.5" />

const RichTextEditor = ({
  content,
  onChange,
  onMediaUpload,
  onImageUpload: onImageUploadCompat,
  placeholder = 'Write something...',
  compact = false,
  label,
  error,
  onSubmit,
  editable = true,
}) => {
  const fileInputRef = useRef(null)
  const videoInputRef = useRef(null)
  const uploadHandler = onMediaUpload || onImageUploadCompat

  const handleMediaUpload = useCallback(async (file) => {
    if (!uploadHandler) return
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    if (!isImage && !isVideo) return
    try {
      const url = await uploadHandler(file)
      if (url && editor) {
        if (isVideo) {
          editor.chain().focus().insertContent({ type: 'video', attrs: { src: url } }).run()
        } else {
          editor.chain().focus().setImage({ src: url }).run()
        }
      }
    } catch (err) {
      console.error('Media upload failed:', err)
    }
  }, [uploadHandler])

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary-600 underline' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      VideoNode,
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML())
    },
    editorProps: {
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files
        if (files?.length) {
          for (const file of files) {
            if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
              event.preventDefault()
              handleMediaUpload(file)
              return true
            }
          }
        }
        return false
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/') || item.type.startsWith('video/')) {
              event.preventDefault()
              const file = item.getAsFile()
              if (file) handleMediaUpload(file)
              return true
            }
          }
        }
        return false
      },
      handleKeyDown: (view, event) => {
        if (event.ctrlKey && event.key === 'Enter' && onSubmit) {
          event.preventDefault()
          onSubmit()
          return true
        }
        return false
      },
    },
  })

  // Sync external content changes
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML()
      // Only update if content actually differs (avoid cursor jump)
      if (content === '' && currentContent !== '<p></p>') {
        editor.commands.clearContent()
      } else if (content && content !== currentContent) {
        editor.commands.setContent(content)
      }
    }
  }, [content, editor])

  const handleLinkClick = useCallback(() => {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('URL', previousUrl)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
  }, [editor])

  if (!editor) return null

  const iconSize = 16

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <div className={`border rounded-lg overflow-hidden ${error ? 'border-red-300' : 'border-gray-300'} focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500`}>
        {/* Toolbar */}
        {editable && (
          <div className="bg-gray-50 border-b border-gray-200 p-1.5 flex flex-wrap gap-0.5">
            <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
              <Bold size={iconSize} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
              <Italic size={iconSize} />
            </ToolbarButton>
            <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
              <UnderlineIcon size={iconSize} />
            </ToolbarButton>
            {!compact && (
              <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
                <Strikethrough size={iconSize} />
              </ToolbarButton>
            )}
            <Divider />
            <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
              <List size={iconSize} />
            </ToolbarButton>
            {!compact && (
              <>
                <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List">
                  <ListOrdered size={iconSize} />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
                  <Quote size={iconSize} />
                </ToolbarButton>
                <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block">
                  <Code size={iconSize} />
                </ToolbarButton>
              </>
            )}
            <Divider />
            <ToolbarButton onClick={handleLinkClick} active={editor.isActive('link')} title="Link">
              <LinkIcon size={iconSize} />
            </ToolbarButton>
            {uploadHandler && (
              <>
                <ToolbarButton onClick={() => fileInputRef.current?.click()} title="Insert Image">
                  <ImageIcon size={iconSize} />
                </ToolbarButton>
                <ToolbarButton onClick={() => videoInputRef.current?.click()} title="Insert Video">
                  <VideoIcon size={iconSize} />
                </ToolbarButton>
              </>
            )}
            <Divider />
            <ToolbarButton
              onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
              title="Clear Formatting"
            >
              <RemoveFormatting size={iconSize} />
            </ToolbarButton>
          </div>
        )}

        {/* Editor Content */}
        <EditorContent editor={editor} className={compact ? 'rich-editor-compact' : 'rich-editor'} />

        {/* Hidden file inputs for media upload */}
        {uploadHandler && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleMediaUpload(file)
                e.target.value = ''
              }}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleMediaUpload(file)
                e.target.value = ''
              }}
            />
          </>
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}

export default RichTextEditor
